#!/usr/bin/env node

import { program } from 'commander'
import chalk from 'chalk'
import figlet from 'figlet'
import { intro, outro } from '@clack/prompts'
import { agentCommand } from './commands/agent.js'
import { marketplaceCommand } from './commands/marketplace.js'
import { escrowCommand } from './commands/escrow.js'
import { channelCommand } from './commands/channel.js'
import { configCommand } from './commands/config.js'
import { setupFaucetCommand } from './commands/faucet.js'
import { sdkCommand } from './commands/sdk.js'
import { updateCommand } from './commands/update.js'
import { auctionCommand } from './commands/auction.js'
import { disputeCommand } from './commands/dispute.js'
import { governanceCommand } from './commands/governance.js'
import { quickstartCommand } from './commands/quickstart.js'
import { checkForUpdates } from './utils/update-check.js'
import { InteractiveMenu, shouldRunInteractive } from './utils/interactive-menu.js'
import { readFileSync, existsSync } from 'fs'
import { join, dirname } from 'path'
import { fileURLToPath } from 'url'
import { homedir } from 'os'

// ASCII art banner
function showBanner(version: string) {
  console.log(
    chalk.cyan(
      figlet.textSync('GhostSpeak', {
        font: 'Small',
        horizontalLayout: 'default',
        verticalLayout: 'default'
      })
    )
  )
  console.log(chalk.gray('AI Agent Commerce Protocol CLI'))
  console.log(chalk.gray(`CLI v${version} | SDK v1.5.0 | Built with ❤️  for Web3 developers\n`))
}

// Main CLI setup
async function main() {
  try {
    
    // Get current version
    let currentVersion = '1.7.2' // Fallback version
    try {
      const __filename = fileURLToPath(import.meta.url)
      const __dirname = dirname(__filename)
      const possiblePaths = [
        join(__dirname, '../package.json'),
        join(__dirname, '../../package.json'),
        join(__dirname, '../../cli/package.json')
      ]
      
      for (const path of possiblePaths) {
        if (existsSync(path)) {
          const pkg = JSON.parse(readFileSync(path, 'utf-8')) as { name?: string; version?: string }
          if (pkg.name === '@ghostspeak/cli' && pkg.version) {
            currentVersion = pkg.version
            break
          }
        }
      }
    } catch (error) {
      // Use fallback version
      void error
    }
    
    // Show banner with version
    showBanner(currentVersion)
    
    // Check for first run
    const configPath = join(homedir(), '.ghostspeak', 'config.json')
    const isFirstRun = !existsSync(configPath)
    
    if (isFirstRun && process.argv.length === 2) {
      // No command specified and first run
      console.log(chalk.yellow('👋 Welcome to GhostSpeak! It looks like this is your first time.'))
      console.log(chalk.cyan('\nLet\'s get you started:'))
      console.log(chalk.gray('  • Run'), chalk.bold.white('gs quickstart'), chalk.gray('for automatic setup'))
      console.log(chalk.gray('  • Run'), chalk.bold.white('gs config setup'), chalk.gray('for manual configuration'))
      console.log(chalk.gray('  • Or run'), chalk.bold.white('gs -i'), chalk.gray('for interactive mode with guided setup'))
      console.log(chalk.gray('  • Run'), chalk.bold.white('gs --help'), chalk.gray('to see all available commands\n'))
    }
    
    // Check for updates in background
    checkForUpdates(currentVersion)
    
    program
      .name('ghostspeak')
      .description('Command-line interface for GhostSpeak AI Agent Commerce Protocol')
      .version(currentVersion)
      .option('-i, --interactive', 'Run in interactive mode')

    // Add command modules - ordered for best first-time UX
    // 1. Essential setup commands first
    program.addCommand(quickstartCommand)  // NEW: Quick start for easy onboarding
    program.addCommand(configCommand)      // Manual setup option
    setupFaucetCommand(program)            // Get SOL to start
    
    // 2. Core features
    program.addCommand(agentCommand)       // Create and manage agents
    program.addCommand(marketplaceCommand) // Use agents in marketplace
    
    // 3. Transaction and payment features
    program.addCommand(escrowCommand)      // Secure payments
    program.addCommand(channelCommand)     // Agent communication
    program.addCommand(auctionCommand)     // Advanced marketplace feature
    
    // 4. Support and advanced features
    program.addCommand(disputeCommand)     // Conflict resolution
    program.addCommand(governanceCommand)  // Protocol governance
    
    // 5. Developer and maintenance tools
    program.addCommand(sdkCommand)         // SDK management
    program.addCommand(updateCommand)      // CLI updates

    // Check if we should run in interactive mode
    if (shouldRunInteractive(process.argv)) {
      // Don't show banner again since it's already shown
      const menu = new InteractiveMenu(program)
      await menu.showMainMenu()
      return
    }

    // Normal CLI mode
    intro(chalk.inverse(' Welcome to GhostSpeak CLI '))

    // Show help if no command provided (shouldn't happen due to interactive check)
    if (!process.argv.slice(2).length) {
      program.outputHelp()
      process.exit(0)
    }

    await program.parseAsync(process.argv)

  } catch (error) {
    console.error(chalk.red('❌ Error:'), error instanceof Error ? error.message : 'Unknown error')
    outro(chalk.red('Operation failed'))
    process.exit(1)
  }
}

// Handle unhandled rejections
process.on('unhandledRejection', (reason, promise) => {
  console.error('Unhandled Rejection at:', promise, 'reason:', reason)
  process.exit(1)
})

// Run the CLI
main().catch((error) => {
  console.error(chalk.red('Fatal error:'), error)
  process.exit(1)
})